import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

interface NewEntitlements {
  product: string;
  siteName: string;
  offeringId: string;
}
interface ExistingEntitlements {
  product: string;
  entitlementId: string;
}

type ScreenKeys =
  | 'addCollectionSetUpScreenEvent'
  | 'addCollectionReviewAndStartTrialScreenEvent'
  | 'addCollectionProvisioningLoadingAndDoneScreenEvent'
  | 'addCollectionLoomDataLocationSelectionScreenEvent'
  | 'addCollectionErrorScreenEvent';

type UIEventKeys =
  | 'backButtonClicked'
  | 'nextButtonClicked'
  | 'getStartedLinkClicked'
  | 'seeAppsListLinkClicked'
  | 'drawerCloseButtonClicked'
  | 'contactSupportButtonClicked';

type TrackEventKeys = 'provisionCollectionCompleted' | 'provisionCollectionStarted' | 'collectionTrialStarted';

export const addCollectionSetUpScreen = 'addCollectionSetUpScreen';
export const addCollectionReviewAndStartTrialScreen = 'addCollectionReviewAndStartTrialScreen';
export const addCollectionProvisioningLoadingAndDoneScreen = 'addCollectionProvisioningLoadingAndDoneScreen';
export const addCollectionErrorScreen = 'addCollectionErrorScreen';
export const addCollectionLoomDataLocationSelectionScreen = 'addCollectionLoomDataLocationSelectionScreen';

export const addCollectionEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec:
    'https://hello.atlassian.net/wiki/spaces/~7120208cbfeee96f8441dea1f49b9ee065b281/pages/4917723927/TwC+Add+Collection+Analytics+Spec',
  screen: {
    addCollectionSetUpScreenEvent: (attributes: { collection: string }): ScreenEvent => ({
      data: {
        name: addCollectionSetUpScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85286',
    }),
    addCollectionReviewAndStartTrialScreenEvent: (attributes: { collection: string; setupMode?: string }): ScreenEvent => ({
      data: {
        name: addCollectionReviewAndStartTrialScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85287',
    }),
    addCollectionProvisioningLoadingAndDoneScreenEvent: (attributes: { collection: string }): ScreenEvent => ({
      data: {
        name: addCollectionProvisioningLoadingAndDoneScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86036',
    }),
    addCollectionErrorScreenEvent: (attributes: { collection: string; errorStatus: string }): ScreenEvent => ({
      data: {
        name: addCollectionErrorScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86038',
    }),
    addCollectionLoomDataLocationSelectionScreenEvent: (attributes: { collection: string }): ScreenEvent => ({
      data: {
        name: addCollectionLoomDataLocationSelectionScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/88310',
    }),
  },
  ui: {
    backButtonClicked: (attributes: { source: string; collection: string; errorStatus?: string; errorReason?: string }): UIEvent => ({
      data: {
        source: attributes.source,
        action: Action.Clicked,
        actionSubjectId: 'back',
        actionSubject: ActionSubject.Button,
        attributes: {
          collection: attributes.collection,
          ...(attributes.errorStatus && { errorStatus: attributes.errorStatus }),
          ...(attributes.errorReason && { errorReason: attributes.errorReason }),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/28051',
    }),
    nextButtonClicked: (attributes: { source: string; collection: string; setupMode?: string }): UIEvent => ({
      data: {
        source: attributes.source,
        action: Action.Clicked,
        actionSubjectId: 'next',
        actionSubject: ActionSubject.Button,
        attributes: {
          collection: attributes.collection,
          ...(attributes.setupMode && { setupMode: attributes.setupMode }),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/25541',
    }),
    getStartedLinkClicked: (attributes: { collection: string }): UIEvent => ({
      data: {
        source: addCollectionProvisioningLoadingAndDoneScreen,
        action: Action.Clicked,
        actionSubjectId: 'getStarted',
        actionSubject: ActionSubject.Link,
        attributes: {
          collection: attributes.collection,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86044',
    }),
    seeAppsListLinkClicked: (attributes: { collection: string }): UIEvent => ({
      data: {
        source: addCollectionProvisioningLoadingAndDoneScreen,
        action: Action.Clicked,
        actionSubjectId: 'seeAppsList',
        actionSubject: ActionSubject.Link,
        attributes: {
          collection: attributes.collection,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86037',
    }),
    drawerCloseButtonClicked: (attributes: { source: string; collection: string }): UIEvent => ({
      data: {
        source: attributes.source,
        action: Action.Clicked,
        actionSubjectId: 'drawerClose',
        actionSubject: ActionSubject.Button,
        attributes: {
          collection: attributes.collection,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/47441',
    }),
    contactSupportButtonClicked: (attributes: {
      source: string;
      collection: string;
      errorStatus: string;
      errorReason: string;
    }): UIEvent => ({
      data: {
        source: attributes.source,
        action: Action.Clicked,
        actionSubjectId: 'contactSupport',
        actionSubject: ActionSubject.Button,
        attributes: {
          collection: attributes.collection,
          errorStatus: attributes.errorStatus,
          errorReason: attributes.errorReason,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72146',
    }),
  },
  track: {
    provisionCollectionStarted: (attributes: {
      collection: string;
      newEntitlements: NewEntitlements[];
      existingEntitlements: ExistingEntitlements[];
    }): TrackEvent => ({
      data: {
        source: addCollectionSetUpScreen,
        action: TrackAction.Started,
        actionSubject: TrackActionSubject.ProvisionCollection,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85859',
    }),
    collectionTrialStarted: (attributes: {
      collection: string;
      newEntitlements: NewEntitlements[];
      existingEntitlements: ExistingEntitlements[];
    }): TrackEvent => ({
      data: {
        source: addCollectionReviewAndStartTrialScreen,
        action: TrackAction.Started,
        actionSubject: TrackActionSubject.CollectionTrial,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85860',
    }),
    provisionCollectionCompleted: (attributes: {
      orgId: string;
      collection: string;
      newEntitlements: NewEntitlements[];
      existingEntitlements: ExistingEntitlements[];
      newLoomOnly: boolean;
    }): TrackEvent => ({
      orgId: attributes.orgId,
      data: {
        source: addCollectionProvisioningLoadingAndDoneScreen,
        action: TrackAction.Completed,
        actionSubject: TrackActionSubject.ProvisionCollection,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85838',
    }),
  },
  operational: {},
};
